import { html2canvas, createJsPdf } from "@/app/report/common/export/lazyLibs"
import { inlineStylesheetsIntoClone } from "@/app/report/common/export/inlineStylesheets"

import { colors } from "@/components/colors"
import { usePdfBuilderStore } from "@/stores/pdfBuilder"
import { exportPngZip } from "@/app/report/common/export/export"

// A4 dimensions in mm
const A4_WIDTH_MM = 210
const A4_HEIGHT_MM = 297

// A4 dimensions in px (96 DPI)
const A4_PX_PORTRAIT = { width: 794, height: 1123 }
const A4_PX_LANDSCAPE = { width: 1123, height: 794 }

export interface CaptureExportOptions {
  filename: string
  quality: "low" | "medium" | "high"
  includePageNumbers: boolean
  onProgress?: (progress: number) => void
}

/**
 * Returns true if a canvas is effectively blank (all pixels close to white).
 * Samples a grid of pixels instead of scanning every pixel for performance.
 */
export function isCanvasBlank(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext("2d")
  if (!ctx) return true

  const { width, height } = canvas
  if (width === 0 || height === 0) return true

  // 표는 흰 바탕에 얇은 글자뿐이라 듬성듬성 찍으면 글자를 다 지나쳐 "비었다" 로 잘못 본다.
  // 1588x2246 기준 예전 간격은 94px 이라 8px 글자(27px 행)를 통째로 건너뛰었다.
  const TARGET_SAMPLES = 40000
  const step = Math.max(1, Math.floor(Math.sqrt((width * height) / TARGET_SAMPLES)))
  for (let y = 0; y < height; y += step) {
    const row = ctx.getImageData(0, y, width, 1).data
    for (let x = 0; x < width; x += step) {
      const i = x * 4
      if (row[i] < 240 || row[i + 1] < 240 || row[i + 2] < 240) {
        return false
      }
    }
  }
  return true
}

/**
 * Capture the full page element (may be taller than A4) and split into
 * page-sized canvas chunks so nothing gets clipped.
 */
export async function capturePageAsCanvasSlices(
  pageElement: HTMLElement,
  quality: "low" | "medium" | "high",
  isLandscapePage: boolean,
  /** 실제로 보이는 내용의 높이(px). 없으면 요소 높이를 쓴다. */
  visibleHeight?: number,
): Promise<HTMLCanvasElement[]> {
  const scale = quality === "high" ? 2 : quality === "medium" ? 1.5 : 1
  const dims = isLandscapePage ? A4_PX_LANDSCAPE : A4_PX_PORTRAIT
  const actualHeight = visibleHeight ?? (pageElement.scrollHeight || pageElement.offsetHeight)

  const fullCanvas = await html2canvas(pageElement, {
    scale,
    useCORS: true,
    onclone: inlineStylesheetsIntoClone,
    allowTaint: true,
    backgroundColor: colors.neutral.white,
    logging: false,
    width: dims.width,
    height: Math.max(actualHeight, dims.height),
    windowWidth: dims.width,
    windowHeight: Math.max(actualHeight, dims.height),
  })

  const scaledPageH = dims.height * scale
  const scaledPageW = dims.width * scale

  // 잘라낸 장은 언제나 A4 한 장 크기여야 한다.
  // 짧은 장을 그대로 넘기면 PDF 에 넣을 때 가로세로 비율을 지키느라 위아래 가운데로 놓여
  // 내용을 화면 맨 위에 붙여 두어도 출력물 상단에 여백이 생긴다.
  const toPageSizedCanvas = (
    source: HTMLCanvasElement,
    offsetY: number,
    sliceH: number,
  ): HTMLCanvasElement => {
    const slice = document.createElement("canvas")
    slice.width = scaledPageW
    slice.height = scaledPageH
    const ctx = slice.getContext("2d")!
    ctx.fillStyle = colors.neutral.white
    ctx.fillRect(0, 0, scaledPageW, scaledPageH)
    ctx.drawImage(source, 0, offsetY, scaledPageW, sliceH, 0, 0, scaledPageW, sliceH)

    return slice
  }

  if (fullCanvas.height <= scaledPageH) {
    if (fullCanvas.height === scaledPageH && fullCanvas.width === scaledPageW) {
      return [fullCanvas]
    }

    return [toPageSizedCanvas(fullCanvas, 0, fullCanvas.height)]
  }

  const slices: HTMLCanvasElement[] = []
  let offsetY = 0
  const minSliceH = scaledPageH * 0.05

  while (offsetY < fullCanvas.height) {
    const remaining = fullCanvas.height - offsetY
    if (remaining < minSliceH) break
    slices.push(toPageSizedCanvas(fullCanvas, offsetY, Math.min(scaledPageH, remaining)))
    offsetY += scaledPageH
  }

  return slices
}

/**
 * Capture an overflow table chunk as a standalone page canvas.
 */
export async function captureOverflowChunk(
  chunkElement: Element,
  quality: "low" | "medium" | "high",
  isLandscape: boolean,
): Promise<HTMLCanvasElement> {
  const scale = quality === "high" ? 2 : quality === "medium" ? 1.5 : 1
  const dims = isLandscape ? A4_PX_LANDSCAPE : A4_PX_PORTRAIT

  // 임시 페이지를 A4 한 장 크기로 잡는다.
  // 높이를 비워 두면 내용만큼만 커지고, 그 캔버스를 페이지에 맞출 때 비율을 지키느라
  // 위아래 가운데로 놓여 상단 여백이 크게 남는다. 페이지 크기로 고정하면 표가 위에서 시작한다.
  const tempPage = document.createElement("div")
  tempPage.style.cssText = `
    width: ${dims.width}px;
    height: ${dims.height}px;
    padding: 40px;
    background: ${colors.neutral.white};
    position: absolute;
    left: -9999px;
    top: 0;
    box-sizing: border-box;
    overflow: hidden;
  `
  tempPage.classList.add("pdf-exporting")

  const canvasItemWrapper = document.createElement("div")
  canvasItemWrapper.className = "CanvasItem"
  canvasItemWrapper.style.cssText = "border: none; padding: 0;"

  const contentWrapper = document.createElement("div")
  contentWrapper.className = "CanvasItem__content"
  contentWrapper.style.cssText = "overflow: visible; padding: 0;"

  const wrapper = document.createElement("div")
  wrapper.className = "BuilderDataTable BuilderDataTable--split"
  wrapper.style.cssText = "width: 100%; overflow: visible;"

  const clonedChunk = chunkElement.cloneNode(true) as HTMLElement
  clonedChunk.classList.remove("BuilderDataTable__chunk--overflow")
  clonedChunk.style.display = "block"
  // 편집 화면에서 장 경계에 맞추려고 넣어 둔 위 여백·바깥 여백·높이는 새 장에서는 뜻이 없다.
  // 그대로 옮기면 그만큼이 윗 여백이 되어 표가 페이지 중간에서 시작한다 (2026-10-02 보고)
  clonedChunk.style.paddingTop = "0"
  clonedChunk.style.marginTop = "0"
  clonedChunk.style.marginBottom = "0"
  clonedChunk.style.minHeight = "0"

  wrapper.appendChild(clonedChunk)
  contentWrapper.appendChild(wrapper)
  canvasItemWrapper.appendChild(contentWrapper)
  tempPage.appendChild(canvasItemWrapper)
  document.body.appendChild(tempPage)

  await new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve(undefined))
    })
  })

  // 캡처 크기도 페이지 한 장으로 고정한다 — 내용 높이로 잡으면 위 설정이 무의미해진다
  const canvas = await html2canvas(tempPage, {
    scale,
    useCORS: true,
    onclone: inlineStylesheetsIntoClone,
    allowTaint: true,
    backgroundColor: colors.neutral.white,
    logging: false,
    width: dims.width,
    height: dims.height,
    windowWidth: dims.width,
    windowHeight: dims.height,
  })

  document.body.removeChild(tempPage)
  return canvas
}

/**
 * 진행률 표시를 실제로 다시 그릴 틈을 준다.
 * 캡처는 한 번에 몇 초씩 걸리는데, 그 사이 화면을 그릴 기회가 없으면 막대가 멈춘 것처럼 보인다.
 */
async function letProgressPaint(): Promise<void> {
  await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)))
}

/**
 * Wait until the canvas area has no loading indicators and content has settled.
 */
export async function waitForDataReady(maxWaitMs = 5000): Promise<void> {
  const start = Date.now()
  const POLL_INTERVAL = 200

  while (Date.now() - start < maxWaitMs) {
    const canvas = document.querySelector(".ReportCanvas")
    if (!canvas) break

    const hasLoading =
      canvas.querySelector(".loading, [class*='Loading' i], .MuiCircularProgress-root") !== null ||
      Array.from(canvas.querySelectorAll("div")).some(
        (el) => el.textContent?.trim() === "Loading..." && el.children.length === 0,
      )

    if (!hasLoading) {
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve(undefined))),
      )
      return
    }

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL))
  }
}

interface PageCapture {
  canvas: HTMLCanvasElement
  isLandscape: boolean
}

/**
 * 편집 화면의 모든 페이지를 A4 한 장짜리 그림들로 캡처한다.
 * PDF 와 PNG 가 같은 결과를 내도록 두 경로가 이 함수 하나를 함께 쓴다.
 *
 * @param progressCeiling 캡처가 끝났을 때의 진행률(%). 남은 구간은 호출한 쪽이 쓴다.
 */
async function capturePages(
  quality: "low" | "medium" | "high",
  progressCeiling: number,
  onProgress?: (progress: number) => void,
): Promise<PageCapture[]> {
  const pages = usePdfBuilderStore.getState().pages
  const setExporting = usePdfBuilderStore.getState().setExporting
  const captures: PageCapture[] = []

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i]

    usePdfBuilderStore.getState().setCurrentPage(page.id)
    await new Promise((resolve) => setTimeout(resolve, 300))
    await waitForDataReady()

    const canvasElement = document.querySelector(".ReportCanvas") as HTMLElement
    if (!canvasElement) continue

    // 이 페이지가 차지하는 진행률 구간. 표가 여러 장으로 나뉘면 캡처 횟수가 그만큼 늘어나므로
    // 한 페이지를 고정 비율로 끊지 않고, 실제 캡처 횟수로 나눠 한 장 끝날 때마다 채운다.
    const pageStart = (i / pages.length) * progressCeiling
    const pageSpan = progressCeiling / pages.length
    let captureSteps = 1
    let captureDone = 0
    const reportCapture = async () => {
      const progress = pageStart + (pageSpan * captureDone) / captureSteps
      setExporting(true, progress)
      onProgress?.(progress)
      await letProgressPaint()
    }
    await reportCapture()

    const isLandscapePage = page.config.orientation === "landscape"
    const pageHeightPx = isLandscapePage ? A4_PX_LANDSCAPE.height : A4_PX_PORTRAIT.height

    const canvasRect = canvasElement.getBoundingClientRect()
    // 편집 화면은 확대/축소를 CSS 변형으로 준다. getBoundingClientRect 는 그 배율이 곱해진
    // 값이라 100% 가 아니면 높이가 그만큼 어긋난다 (50% 면 절반만 캡처돼 내용이 잘린다).
    // 변형의 영향을 받지 않는 offsetWidth 와 견줘 배율을 되돌린다.
    const zoomScale =
      canvasElement.offsetWidth > 0 ? canvasRect.width / canvasElement.offsetWidth : 1
    const canvasTop = canvasRect.top
    const toCanvasY = (clientY: number) => (clientY - canvasTop) / (zoomScale || 1)

    // 표의 둘째 덩어리부터는 따로 한 장씩 찍는다. 찍은 장을 뒤에 몰아 붙이면
    // 표 아래에 둔 컴포넌트가 표 뒷장보다 먼저 나온다. 편집 화면에서 몇 번째 장에 놓였는지를
    // 숨기기 전에 재 두고, 마지막에 그 순서대로 늘어놓는다.
    const overflowChunks: { element: HTMLElement; pageIndex: number }[] = []
    const allTables = canvasElement.querySelectorAll(".BuilderDataTable")
    allTables.forEach((table) => {
      const chunks = table.querySelectorAll(".BuilderDataTable__chunk")
      chunks.forEach((chunk, chunkIdx) => {
        if (chunkIdx > 0) {
          const element = chunk as HTMLElement
          const top = toCanvasY(element.getBoundingClientRect().top)
          overflowChunks.push({ element, pageIndex: Math.round(top / pageHeightPx) })
        }
      })
    })

    // 같은 장을 두 덩어리가 가리키면(배치가 어긋난 경우) 뒤엣것을 다음 장으로 민다
    overflowChunks.sort((a, b) => a.pageIndex - b.pageIndex)
    let lastIndex = -1
    overflowChunks.forEach((chunk) => {
      chunk.pageIndex = Math.max(chunk.pageIndex, lastIndex + 1)
      lastIndex = chunk.pageIndex
    })

    overflowChunks.forEach((chunk) => {
      chunk.element.style.display = "none"
    })

    // 기본 캡처 1회 + 넘친 덩어리 수만큼 캡처한다
    captureSteps = 1 + overflowChunks.length

    // 덩어리를 숨겨도 격자 칸 높이는 그대로라, 그대로 캡처하면 빈 공간까지 포함해
    // 캔버스가 수천 px 로 커진다. 큰 캔버스는 브라우저 한계에 걸려 통째로 비어 나올 수 있고,
    // 빈 장이 늘어난다. 실제로 보이는 내용의 아래 끝까지만 캡처한다.
    let visibleBottom = 0
    canvasElement.querySelectorAll<HTMLElement>(".react-grid-item").forEach((gridItem) => {
      const visibleChunks = [
        ...gridItem.querySelectorAll<HTMLElement>(".BuilderDataTable__chunk"),
      ].filter((chunk) => chunk.offsetParent !== null)
      const bottom = visibleChunks.length
        ? Math.max(...visibleChunks.map((chunk) => chunk.getBoundingClientRect().bottom))
        : gridItem.getBoundingClientRect().bottom
      visibleBottom = Math.max(visibleBottom, toCanvasY(bottom))
    })

    // 편집 화면에 놓인 순서대로 모은다 (pageIndex = 몇 번째 장인지)
    const ordered: { pageIndex: number; canvas: HTMLCanvasElement }[] = []

    const pageSlices = await capturePageAsCanvasSlices(
      canvasElement,
      quality,
      isLandscapePage,
      visibleBottom > 0 ? Math.ceil(visibleBottom) : undefined,
    )
    pageSlices.forEach((slice, sliceIdx) => {
      if (!isCanvasBlank(slice)) {
        ordered.push({ pageIndex: sliceIdx, canvas: slice })
      }
    })

    captureDone++
    await reportCapture()

    for (const chunk of overflowChunks) {
      // 오버플로 덩어리는 존재 자체가 내용이 있다는 뜻이므로 빈 그림 판정을 하지 않는다.
      // 표처럼 글자가 성긴 그림은 잘못 버려진다 (9장 중 7장이 버려지던 문제)
      const chunkCanvas = await captureOverflowChunk(chunk.element, quality, isLandscapePage)
      ordered.push({ pageIndex: chunk.pageIndex, canvas: chunkCanvas })
      captureDone++
      await reportCapture()
    }

    overflowChunks.forEach((chunk) => {
      chunk.element.style.display = ""
    })

    ordered.sort((a, b) => a.pageIndex - b.pageIndex)
    ordered.forEach(({ canvas }) => captures.push({ canvas, isLandscape: isLandscapePage }))
  }

  return captures
}

/**
 * 내보내기 준비/뒷정리를 감싼다. 진행 표시를 켜고, 편집 전용 UI 를 숨기고,
 * 끝나면 보던 페이지로 되돌린다.
 */
async function withExportMode<T>(run: () => Promise<T>): Promise<T> {
  const setExporting = usePdfBuilderStore.getState().setExporting
  const originalPageId = usePdfBuilderStore.getState().currentPageId

  document.body.classList.add("pdf-exporting")
  setExporting(true, 0)

  try {
    return await run()
  } finally {
    if (originalPageId) {
      usePdfBuilderStore.getState().setCurrentPage(originalPageId)
    }
    document.body.classList.remove("pdf-exporting")
    setExporting(false, 0)
  }
}

/**
 * Main orchestrator: iterate pages → capture → assemble jsPDF → save.
 *
 * Expects `usePdfBuilderStore` to already have pages/items/filters set,
 * and a `.ReportCanvas` element rendered in the DOM.
 */
export async function exportPagesToPdf(options: CaptureExportOptions): Promise<void> {
  const { filename, quality, includePageNumbers, onProgress } = options
  const setExporting = usePdfBuilderStore.getState().setExporting

  await withExportMode(async () => {
    const pages = usePdfBuilderStore.getState().pages
    const firstPage = pages[0]
    const isLandscape = firstPage?.config.orientation === "landscape"

    const doc = await createJsPdf({
      orientation: isLandscape ? "landscape" : "portrait",
      unit: "mm",
      format: "a4",
    })

    // Pretendard(커스텀) 폰트는 추가하지 않는다.
    // jsPDF 는 출력 시 추가된 모든 커스텀 폰트를 임베드/서브셋하는데, CDN 로드 실패/OTF 등으로
    // 글리프 데이터가 없으면 glyphFor 에서 크래시해 PDF 생성이 통째로 실패한다.
    // 본문은 전부 이미지, 텍스트는 페이지번호(숫자)뿐이라 내장 helvetica 로 충분.
    doc.setFont("helvetica", "normal")

    const captures = await capturePages(quality, 80, onProgress)

    // Build PDF from captures
    const totalPdfPages = captures.length
    for (let p = 0; p < totalPdfPages; p++) {
      const { canvas, isLandscape: isLandscapePage } = captures[p]
      const pdfWidth = isLandscapePage ? A4_HEIGHT_MM : A4_WIDTH_MM
      const pdfHeight = isLandscapePage ? A4_WIDTH_MM : A4_HEIGHT_MM

      if (p > 0) {
        doc.addPage("a4", isLandscapePage ? "l" : "p")
      }

      const canvasAspectRatio = canvas.width / canvas.height
      const pageAspectRatio = pdfWidth / pdfHeight

      let imgWidth = pdfWidth
      let imgHeight = pdfHeight
      let imgX = 0
      let imgY = 0

      if (canvasAspectRatio > pageAspectRatio) {
        imgHeight = pdfWidth / canvasAspectRatio
        imgY = (pdfHeight - imgHeight) / 2
      } else {
        imgWidth = pdfHeight * canvasAspectRatio
        imgX = (pdfWidth - imgWidth) / 2
      }

      const imgData = canvas.toDataURL("image/png")
      doc.addImage(imgData, "PNG", imgX, imgY, imgWidth, imgHeight)

      if (includePageNumbers) {
        doc.setFontSize(10)
        doc.setTextColor(128)
        doc.text(`${p + 1} / ${totalPdfPages}`, pdfWidth / 2, pdfHeight - 10, { align: "center" })
      }

      const progress = 80 + ((p + 1) / totalPdfPages) * 20
      setExporting(true, progress)
      onProgress?.(progress)
      await letProgressPaint()
    }

    const outputFilename = `${filename || "report"}.pdf`
    doc.save(outputFilename)
  })
}

/**
 * 같은 캡처 결과를 PNG 로 내보낸다.
 * 여러 장일 때 장마다 따로 내려받으면 브라우저가 두 번째부터 막으므로 zip 한 개로 묶는다.
 */
export async function exportPagesToPng(options: CaptureExportOptions): Promise<void> {
  const { filename, quality, onProgress } = options
  const setExporting = usePdfBuilderStore.getState().setExporting
  const name = filename || "report"

  await withExportMode(async () => {
    const captures = await capturePages(quality, 90, onProgress)
    if (captures.length === 0) {
      return
    }

    setExporting(true, 95)
    onProgress?.(95)
    await letProgressPaint()

    if (captures.length === 1) {
      const link = document.createElement("a")
      link.download = `${name}.png`
      link.href = captures[0].canvas.toDataURL("image/png")
      link.click()
    } else {
      const digits = String(captures.length).length
      exportPngZip(
        `${name}.zip`,
        captures.map((capture, index) => ({
          canvas: capture.canvas,
          fileName: `${name}_${String(index + 1).padStart(digits, "0")}`,
        })),
      )
    }

    setExporting(true, 100)
    onProgress?.(100)
  })
}
